/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { colors } from '../../../shared/theme/colors'
import { ticketColors } from '../../../shared/theme/ticketColors';
import { spacing } from '../../../shared/theme/spacing';
import { styles } from '../posShared.styles';
import { ModalShell } from '../../../shared/modals/ModalShell'
import { ModalFormField } from '../../../shared/modals/ModalFormField'
import { modalExperience } from '../../../shared/modals/modalExperience.styles';
import { parseDecimalInput } from '../../../shared/numeric/localeNumeric';
import { NumericTextInput } from '../../../shared/ui/NumericTextInput';
import { OperationalFilterChipGroup } from '../../../shared/filters/OperationalFilterPrimitives';
import type { ExternalServicePaymentMethod, ExternalServiceType } from '../../../shell/externalServiceRepo';

type PosRecargaModalProps = {
  visible: boolean;
  serviceType: ExternalServiceType;
  amount: string;
  reference: string;
  paymentMethod: ExternalServicePaymentMethod;
  onClose: () => void;
  onChangeServiceType: (value: ExternalServiceType) => void;
  onChangeAmount: (value: string) => void;
  onChangeReference: (value: string) => void;
  onChangePaymentMethod: (value: ExternalServicePaymentMethod) => void;
  onSave: () => void | Promise<void>;
  saving?: boolean;
};

export const PosRecargaModal = ({
  visible,
  serviceType,
  amount,
  reference,
  paymentMethod,
  onClose,
  onChangeServiceType,
  onChangeAmount,
  onChangeReference,
  onChangePaymentMethod,
  onSave,
  saving = false,
}: PosRecargaModalProps) => {
  const [saveAttempted, setSaveAttempted] = useState(false);
  const trimmedAmount = amount.trim();
  const parsedAmount = trimmedAmount === '' ? null : parseDecimalInput(amount, { min: 0 });
  const amountInvalid = trimmedAmount.length === 0 || parsedAmount === null || parsedAmount <= 0;
  const showAmountError = saveAttempted && amountInvalid;

  useEffect(() => {
    if (!visible) setSaveAttempted(false);
  }, [visible]);

  useEffect(() => {
    if (!amountInvalid) setSaveAttempted(false);
  }, [amountInvalid]);

  const amountHelperText = useMemo(() => {
    if (!showAmountError) return null;
    if (trimmedAmount.length === 0) return 'Ingresá un monto';
    if (parsedAmount === null) return 'Monto inválido';
    return 'El monto debe ser mayor a 0';
  }, [parsedAmount, showAmountError, trimmedAmount.length]);

  const handleSavePress = () => {
    if (saving) return;
    if (amountInvalid) {
      setSaveAttempted(true);
      return;
    }
    onSave();
  };

  return (
    <ModalShell
      visible={visible}
      onClose={onClose}
      onRequestClose={saving ? undefined : onClose}
      title="Recarga"
      subtitle="SUBE o carga virtual. No es del catálogo."
      closeAccessibilityLabel="Cerrar recarga"
      scrollable={false}
      footer={
        <View style={[styles.modalActions, modalExperience.footerActions]}>
          <TouchableOpacity
            style={[styles.secondaryButton, styles.modalButtonHalf]}
            onPress={onClose}
            disabled={saving}
            accessibilityRole="button"
            accessibilityLabel="Cancelar recarga"
          >
            <Text style={styles.secondaryButtonText}>Cancelar</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.posPrimaryCta, styles.modalButtonHalf, saving && { opacity: 0.6 }]}
            onPress={handleSavePress}
            disabled={saving}
            accessibilityRole="button"
            accessibilityLabel="Registrar recarga"
            testID="pos-recarga-save"
          >
            {saving ? <ActivityIndicator color={ticketColors.goldInk} /> : <Text style={styles.posPrimaryCtaText}>Registrar</Text>}
          </TouchableOpacity>
        </View>
      }
    >
      <View style={modalExperience.formPanel}>
        <ModalFormField label="Tipo">
          <OperationalFilterChipGroup
            options={[
              { value: 'sube', label: 'SUBE' },
              { value: 'carga_virtual', label: 'Carga virtual' },
            ]}
            value={serviceType}
            onChange={onChangeServiceType}
          />
        </ModalFormField>
        <ModalFormField label="Monto" required>
          <NumericTextInput
            kind="decimalMoney"
            value={amount}
            onChangeText={onChangeAmount}
            placeholder="0,00"
            placeholderTextColor={colors.textMuted}
            style={[modalExperience.formInput, showAmountError && { borderColor: colors.warning }]}
            returnKeyType="done"
            editable={!saving}
          />
          {amountHelperText ? (
            <Text
              style={[
                modalExperience.formLabel,
                { fontWeight: '500' },
                showAmountError && { color: colors.warning },
              ]}
            >
              {amountHelperText}
            </Text>
          ) : null}
        </ModalFormField>
        <ModalFormField label="Cobro">
          <OperationalFilterChipGroup
            options={[
              { value: 'cash', label: 'Efectivo' },
              { value: 'transfer', label: 'Transferencia' },
            ]}
            value={paymentMethod}
            onChange={onChangePaymentMethod}
          />
          {paymentMethod === 'cash' ? (
            <Text style={[modalExperience.formLabel, { marginTop: spacing.xs, fontWeight: '500' }]}>
              Hace falta caja abierta. Entra al arqueo del turno.
            </Text>
          ) : null}
        </ModalFormField>
        <ModalFormField label="Referencia (opcional)">
          <TextInput
            value={reference}
            onChangeText={onChangeReference}
            placeholder="Nº de operación"
            placeholderTextColor={colors.textMuted}
            style={modalExperience.formInput}
            returnKeyType="done"
            editable={!saving}
            accessibilityLabel="Referencia de la recarga"
          />
        </ModalFormField>
      </View>
    </ModalShell>
  );
};
